/**
 * next/navigation shim for Vite + TanStack Router
 *
 * Same API surface as Next.js's next/navigation, backed by @tanstack/react-router.
 * Shared components import from '@/lib/router'; Vite's resolve.alias redirects
 * here transparently — no changes needed in any shared file.
 */
import { useMemo } from 'react'
import {
  useNavigate as useTanstackNavigate,
  useLocation,
  useParams as useTanstackParams,
  useSearch,
  useRouter as useTanstackRouter,
} from '@tanstack/react-router'

// ─── useRouter ────────────────────────────────────────────────────────────────
// Returns a stable object — shared code puts `router` in useEffect deps.
export function useRouter() {
  const navigate = useTanstackNavigate()
  const routerInstance = useTanstackRouter()
  return useMemo(
    () => ({
      push: (href: string, _options?: Record<string, unknown>) =>
        navigate({ to: href as string }),
      replace: (href: string, _options?: Record<string, unknown>) =>
        navigate({ to: href as string, replace: true }),
      back: () => routerInstance.history.back(),
      forward: () => routerInstance.history.forward(),
      refresh: () => window.location.reload(),
      prefetch: (_href: string) => {
        // TanStack Router prefetches on hover automatically — no-op here
      },
    }),
    [navigate, routerInstance],
  )
}

// ─── usePathname ──────────────────────────────────────────────────────────────
export function usePathname(): string {
  const { pathname } = useLocation()
  return pathname
}

// ─── useSearchParams ──────────────────────────────────────────────────────────
// Next.js returns URLSearchParams directly.
// TanStack Router returns a typed object — we convert it here.
export function useSearchParams(): URLSearchParams {
  const search = useSearch({ strict: false })
  return new URLSearchParams(
    Object.entries(search as Record<string, unknown>)
      .filter(([, v]) => v !== undefined && v !== null)
      .map(([k, v]) => [k, String(v)]),
  )
}

// ─── useParams ───────────────────────────────────────────────────────────────
export function useParams(): Record<string, string> {
  return useTanstackParams({ strict: false }) as Record<string, string>
}

// ─── redirect ────────────────────────────────────────────────────────────────
// In Next.js this is server-side; here it is always client-side.
export function redirect(href: string): never {
  window.location.replace(href)
  throw new Error(`Redirecting to ${href}`)
}

// ─── notFound ────────────────────────────────────────────────────────────────
export function notFound(): never {
  throw new Error('NOT_FOUND')
}
